import { AppState } from 'src/AppState';
import AppStateView from 'src/AppStateView';
import {
	EasyClaimAppState,
	EasyClaimDocumentType,
} from 'src/easyclaim.api.client.typescript/src/index';
import { CustomerJourneyQuestionState } from 'src/easyclaim.app.services/CustomerJourneyQuestionState';
import { Image } from 'src/easyclaim.app.services/ImageSelectionService';

interface RequiredPhoto {
	image?: Image;
	descriptionId: string;
	// Photo is shown as correct regardless of its validation results
	isAccepted?: boolean;
}

export interface ScreenGroupImage {
	image?: Image;
	descriptionId: string;
	stepAppState: EasyClaimAppState;
	isAccepted?: boolean;
}

export function isReviewImageCompleted(appState: AppState, photo: ScreenGroupImage): boolean {
	return !!photo.isAccepted || appState.appService.isImageSelectionCompleted(photo.image);
}

export function hasReviewImageWarning(appState: AppState, photo: ScreenGroupImage): boolean {
	return !photo.isAccepted && appState.appService.hasAnyWarningsOrErrors([photo.image]);
}

export interface ScreenGroupQuestion {
	question: CustomerJourneyQuestionState;
	stepAppState: EasyClaimAppState;
}

export interface ScreenGroup {
	groupKey: string;
	groupId: string | null;
	screens: AppStateView[];
	images?: ScreenGroupImage[];
	questions?: ScreenGroupQuestion[];
}

function getRequiredQuestions(
	appState: AppState,
	appStateEnum: EasyClaimAppState
): CustomerJourneyQuestionState[] | undefined {
	switch (appStateEnum) {
		case EasyClaimAppState.Questions:
			return appState.cjQuestions?.length > 0 ? appState.cjQuestions : undefined;
		default:
			return undefined;
	}
}

// A manual-input photo is no longer required, but it was uploaded and should still be reviewable
function isDashboardImageShownOnReview(
	appState: AppState,
	appStateEnum: EasyClaimAppState
): boolean {
	return (
		appState.appService.isDashboardImageRequired(appStateEnum) ||
		isDashboardImageAcceptedAsManualInput(appState)
	);
}

// After manual input the mileage is typed by the user, so the photo's recognition problems no longer matter
function isDashboardImageAcceptedAsManualInput(appState: AppState): boolean {
	return appState.appService.isDashboardImageFlaggedAsManualInput() && !!appState.dashboardImage;
}

function getDashboardReviewPhoto(appState: AppState): RequiredPhoto {
	return {
		image: appState.dashboardImage,
		descriptionId: 'review.stepDescription.mileage',
		isAccepted: isDashboardImageAcceptedAsManualInput(appState),
	};
}

function getRequiredPhotos(
	appState: AppState,
	appStateEnum: EasyClaimAppState
): RequiredPhoto[] | undefined {
	switch (appStateEnum) {
		case EasyClaimAppState.FourDiagonalPerspectives: {
			const requiredPhotos: RequiredPhoto[] = [];

			if (
				appState.appService.isDocumentTypeImageControlVisible(
					appStateEnum,
					EasyClaimDocumentType.FrontLeftPicture
				)
			)
				requiredPhotos.push({
					image: appState.vehicleImageFrontLeft,
					descriptionId: 'review.stepDescription.vehicleFrontLeftPerspective',
				});
			if (
				appState.appService.isDocumentTypeImageControlVisible(
					appStateEnum,
					EasyClaimDocumentType.RearLeftPicture
				)
			)
				requiredPhotos.push({
					image: appState.vehicleImageRearLeft,
					descriptionId: 'review.stepDescription.vehicleRearLeftPerspective',
				});
			if (
				appState.appService.isDocumentTypeImageControlVisible(
					appStateEnum,
					EasyClaimDocumentType.RearRightPicture
				)
			)
				requiredPhotos.push({
					image: appState.vehicleImageRearRight,
					descriptionId: 'review.stepDescription.vehicleRearRightPerspective',
				});
			if (
				appState.appService.isDocumentTypeImageControlVisible(
					appStateEnum,
					EasyClaimDocumentType.FrontRightPicture
				)
			)
				requiredPhotos.push({
					image: appState.vehicleImageFrontRight,
					descriptionId: 'review.stepDescription.vehicleFrontRightPerspective',
				});

			return requiredPhotos.length > 0 ? requiredPhotos : undefined;
		}
		case EasyClaimAppState.FourAdditionalPictures:
		case EasyClaimAppState.FourAdditionalPicturesAZCEE: {
			const requiredPhotos: RequiredPhoto[] = [];

			if (appState.appService.isOverallDashboardImageRequired(appStateEnum))
				requiredPhotos.push({
					image: appState.overallDashboardImage,
					descriptionId: 'review.stepDescription.overallDashboard',
				});
			if (isDashboardImageShownOnReview(appState, appStateEnum))
				requiredPhotos.push(getDashboardReviewPhoto(appState));
			if (appState.appService.isWindshieldImageRequired(appStateEnum))
				requiredPhotos.push({
					image: appState.windshieldImage,
					descriptionId: 'review.stepDescription.windshield',
				});
			if (appState.appService.isVinImageRequired(appStateEnum))
				requiredPhotos.push({
					image: appState.vinImage,
					descriptionId: 'review.stepDescription.vinPicture',
				});

			return requiredPhotos.length > 0 ? requiredPhotos : undefined;
		}
		case EasyClaimAppState.DamageImages: {
			const requiredPhotos: RequiredPhoto[] = [];
			const isDamageRequired = appState.appService.isDocumentTypeImageControlRequired(
				appStateEnum,
				EasyClaimDocumentType.DetailPicture
			);

			if (isDamageRequired || appState.closeUpDamageImage)
				requiredPhotos.push({
					image: appState.closeUpDamageImage,
					descriptionId: 'review.stepDescription.damageImageCloseUp',
				});
			if (isDamageRequired || appState.overallDamageImage)
				requiredPhotos.push({
					image: appState.overallDamageImage,
					descriptionId: 'review.stepDescription.damageImageOverall',
				});
			appState.additionalDamageImages.forEach((img) =>
				requiredPhotos.push({
					image: img,
					descriptionId: 'review.stepDescription.damageImageAdditional',
				})
			);

			return requiredPhotos.length > 0 ? requiredPhotos : undefined;
		}
		case EasyClaimAppState.AdditionalDamagePictures: {
			const additionalDamagePhotos: RequiredPhoto[] = [];

			if (appState.closeUpDamageImage) {
				additionalDamagePhotos.push({
					image: appState.closeUpDamageImage,
					descriptionId: 'review.stepDescription.damageImageCloseUp',
				});
			}
			if (appState.overallDamageImage) {
				additionalDamagePhotos.push({
					image: appState.overallDamageImage,
					descriptionId: 'review.stepDescription.damageImageOverall',
				});
			}
			appState.additionalDamageImages.forEach((img) =>
				additionalDamagePhotos.push({
					image: img,
					descriptionId: 'review.stepDescription.damageImageAdditional',
				})
			);

			return additionalDamagePhotos.length > 0 ? additionalDamagePhotos : undefined;
		}
		case EasyClaimAppState.VehicleFrontLeftPerspectiveImage:
			return [
				{
					image: appState.vehicleImageFrontLeft,
					descriptionId: 'review.stepDescription.vehicleFrontLeftPerspective',
				},
			];
		case EasyClaimAppState.VehicleRearLeftPerspectiveImage:
			return [
				{
					image: appState.vehicleImageRearLeft,
					descriptionId: 'review.stepDescription.vehicleRearLeftPerspective',
				},
			];
		case EasyClaimAppState.VehicleRearRightPerspectiveImage:
			return [
				{
					image: appState.vehicleImageRearRight,
					descriptionId: 'review.stepDescription.vehicleRearRightPerspective',
				},
			];
		case EasyClaimAppState.VehicleFrontRightPerspectiveImage:
			return [
				{
					image: appState.vehicleImageFrontRight,
					descriptionId: 'review.stepDescription.vehicleFrontRightPerspective',
				},
			];
		case EasyClaimAppState.VehicleFrontPerspectiveImage:
			return [
				{
					image: appState.vehicleImageFront,
					descriptionId: 'review.stepDescription.vehicleFrontPerspectiveImage',
				},
			];
		case EasyClaimAppState.VehicleRightPerspectiveImage:
			return [
				{
					image: appState.vehicleImageRight,
					descriptionId: 'review.stepDescription.vehicleRightPerspectiveImage',
				},
			];
		case EasyClaimAppState.VehicleRearPerspectiveImage:
			return [
				{
					image: appState.vehicleImageRear,
					descriptionId: 'review.stepDescription.vehicleRearPerspectiveImage',
				},
			];
		case EasyClaimAppState.VehicleLeftPerspectiveImage:
			return [
				{
					image: appState.vehicleImageLeft,
					descriptionId: 'review.stepDescription.vehicleLeftPerspectiveImage',
				},
			];
		case EasyClaimAppState.DashboardMileage: {
			if (!isDashboardImageShownOnReview(appState, EasyClaimAppState.DashboardMileage)) {
				return undefined;
			}

			return [getDashboardReviewPhoto(appState)];
		}
		case EasyClaimAppState.OverallDashboardImage:
			return [
				{
					image: appState.overallDashboardImage,
					descriptionId: 'review.stepDescription.overallDashboard',
				},
			];
		case EasyClaimAppState.WindshieldImage:
			return [
				{ image: appState.windshieldImage, descriptionId: 'review.stepDescription.windshield' },
			];
		case EasyClaimAppState.VinPicture:
			return [{ image: appState.vinImage, descriptionId: 'review.stepDescription.vinPicture' }];
		case EasyClaimAppState.VehicleRegistrationDocument: {
			const images = new Map<EasyClaimDocumentType, RequiredPhoto>([
				[
					EasyClaimDocumentType.RegistrationDocument,
					{
						image: appState.registrationDocument,
						descriptionId: 'review.stepDescription.registrationDocument',
					},
				],
				[
					EasyClaimDocumentType.RegistrationDocumentInside,
					{
						image: appState.registrationDocument2,
						descriptionId: 'review.stepDescription.registrationDocumentInside',
					},
				],
			]);

			const requiredPhotos = appState.appService
				.getControlsConfigurations(appStateEnum)
				.map(({ documentType }) => (documentType ? images.get(documentType) : undefined))
				.filter((photo): photo is RequiredPhoto => !!photo);

			return requiredPhotos.length > 0 ? requiredPhotos : undefined;
		}
		default:
			return undefined;
	}
}

export function getReviewImagesForScreen(
	appState: AppState,
	appStateEnum: EasyClaimAppState
): ScreenGroupImage[] {
	return (
		getRequiredPhotos(appState, appStateEnum)?.map((photo) => ({
			...photo,
			stepAppState: appStateEnum,
		})) ?? []
	);
}

export function buildReviewGroups(appState: AppState): ScreenGroup[] {
	const configuration = appState.easyClaimCase?._configuration;

	if (!configuration?.frontendSteps) return [];
	const steps = configuration.frontendSteps.map((fs) => fs.appState!);
	const stepOrder = new Map(steps.map((step, index) => [step, index]));

	const filteredSortedSteps = appState.appStateViews
		.filter(
			(step) =>
				!!step.appStateReviewDescriptionId &&
				stepOrder.has(step.appState) &&
				!!step.appStateReviewDescriptionId(appState)
		)
		.sort(
			(a, b) =>
				(stepOrder.get(a.appState) ?? Number.MAX_SAFE_INTEGER) -
				(stepOrder.get(b.appState) ?? Number.MAX_SAFE_INTEGER)
		);

	const groups: ScreenGroup[] = [];
	const groupsById = new Map<string, ScreenGroup>();

	for (const step of filteredSortedSteps) {
		const groupId = step.appStateReviewGroupId ? step.appStateReviewGroupId(appState) : null;
		const images = getReviewImagesForScreen(appState, step.appState);
		const questions = getRequiredQuestions(appState, step.appState)?.map((q) => ({
			question: q,
			stepAppState: step.appState,
		}));

		if (groupId !== null) {
			const existing = groupsById.get(groupId);

			if (existing) {
				existing.screens.push(step);
				if (images.length > 0) existing.images = [...(existing.images ?? []), ...images];
				if (questions) existing.questions = [...(existing.questions ?? []), ...questions];
				continue;
			}
		}

		const group = {
			groupKey: groupId ?? `screen-${step.appState}`,
			groupId,
			screens: [step],
			images: images.length > 0 ? images : undefined,
			questions: questions && questions.length > 0 ? questions : undefined,
		};

		groups.push(group);
		if (groupId !== null) {
			groupsById.set(groupId, group);
		}
	}

	return groups;
}

export function isReviewGroupedCompleted(appState: AppState): boolean {
	const groups = buildReviewGroups(appState);

	return groups.every((group) => {
		const images = group.images;

		if (images && images.length > 0) {
			return images.every((p) => isReviewImageCompleted(appState, p));
		}

		const questions = group.questions;

		if (questions && questions.length > 0) {
			return questions.every((q) => !q.question.required || q.question.answer !== undefined);
		}

		return group.screens.every((s) => !s.canMoveNext || s.canMoveNext(appState));
	});
}
